// Run the whole scenario suite from the admin, with one click.
//
// ⚠️ THE BROWSER DRIVES THE LOOP, ONE SCENARIO PER REQUEST. Twenty-one model
// calls in a single worker invocation is about a minute and a half of
// subrequests; a timeout would lose every result already paid for. Looping here
// means progress is visible as it goes and a failure costs one scenario.
//
// ⚠️ IT SPENDS REAL MONEY, so the cost is stated before the first click and the
// run can be stopped. Nothing is ever sent to WhatsApp — `/simulate` builds a
// draft and returns it.
import { useState, useRef } from 'react';
import { SettingGroup } from './_ui';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { whatsappApi, type ScenarioDef, type ScenarioRun } from '@/lib/api';
import { useT } from '@/lib/i18n';

type Row = { def: ScenarioDef; run?: ScenarioRun; error?: string };

const failures = (r?: ScenarioRun) => (r?.results ?? []).filter((x) => !x.pass && !x.skipped);

function Chip({ rule, pass, skipped, detail, confidence }: {
  rule: string; pass: boolean; skipped?: boolean; detail: string; confidence: string;
}) {
  const cls = skipped
    ? 'bg-gray-100 text-gray-500 border-gray-200'
    : pass
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-red-50 text-red-700 border-red-200 font-semibold';
  return (
    <span
      title={detail || rule}
      // ⚠️ Dashed = keyword check. A guess must not look like a measurement.
      className={`text-[10.5px] px-2 py-[2px] rounded-full border ${cls} ${
        confidence === 'weak' && !skipped ? 'border-dashed' : ''
      }`}
    >
      {skipped ? '–' : pass ? '✓' : '✕'} {rule}
    </span>
  );
}

export function ScenarioTests() {
  const { t } = useT();
  const [rows, setRows] = useState<Row[]>([]);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const stop = useRef(false);

  const defs = useQuery({
    queryKey: ['scenario-defs'],
    queryFn: () => whatsappApi.scenarios(),
    staleTime: 5 * 60_000,
  });
  const list = defs.data?.scenarios ?? [];

  async function runAll() {
    stop.current = false;
    setRunning(true);
    setDone(0);
    setRows(list.map((def) => ({ def })));
    for (const [i, def] of list.entries()) {
      if (stop.current) break;
      try {
        const run = await whatsappApi.runScenario(def.id);
        setRows((prev) => prev.map((r, j) => (j === i ? { ...r, run } : r)));
      } catch (e) {
        // One dead scenario must not end the run — the rest are still worth
        // paying for.
        setRows((prev) => prev.map((r, j) => (j === i ? { ...r, error: (e as Error).message } : r)));
      }
      setDone(i + 1);
    }
    setRunning(false);
  }

  const bad = rows.filter((r) => r.error || failures(r.run).length);
  const graded = rows.filter((r) => r.run || r.error);

  return (
    // ⚠️ THE SUMMARY IS THE LAST RESULT, NOT A DESCRIPTION. This card sat open
    // above the chat with two paragraphs explaining what scenario tests are —
    // read once, then noise forever. What is worth seeing at a glance is
    // whether the last run passed, and that was buried below the fold.
    //
    // ⚠️ FORCED OPEN WHILE RUNNING. A run takes minutes and costs real money;
    // collapsing progress out of sight is how somebody starts a second one.
    <SettingGroup
      title={t('Tests de scénarios')}
      defaultOpen={running}
      summary={
        running
          ? `${t('en cours')} — ${done}/${list.length}`
          : rows.length
            ? `${rows.filter((r) => r.run && !failures(r.run).length).length}/${rows.length} ${t('réussis')}`
            : t('jamais lancés · environ 0,40 $ pour tout lancer')
      }
      tone={!running && rows.some((r) => r.run && failures(r.run).length) ? 'sensitive' : 'default'}
    >
    <div>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="text-[13px] font-semibold text-gray-800">{t('Tests de scénarios')}</h3>
          <p className="text-[11.5px] text-gray-500 mt-0.5 leading-snug">
            {t('Des conversations types sont envoyées à l’assistant, puis notées sur les règles : la langue du client, une seule question, aucun chiffre inventé, passer la main quand il faut.')}
          </p>
          <p className="text-[11px] text-amber-700 mt-1">
            {t('Chaque test appelle l’IA : environ 0,40 $ pour tout lancer. Rien n’est envoyé sur WhatsApp.')}
          </p>
        </div>
        {running
          ? (
            <Button size="sm" variant="secondary" onClick={() => { stop.current = true; }}>
              {t('Arrêter')}
            </Button>
          )
          : (
            <Button size="sm" onClick={runAll} disabled={!list.length}>
              {t('Lancer les tests')}
            </Button>
          )}
      </div>

      {running && (
        <div className="mt-3">
          <div className="h-[6px] bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-[width] duration-300"
              style={{ width: `${list.length ? (done / list.length) * 100 : 0}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-500 mt-1 tabular-nums">
            {t('{a} sur {b}', { a: String(done), b: String(list.length) })}
          </p>
        </div>
      )}

      {graded.length > 0 && (
        <p className="text-[12px] mt-3 font-semibold text-gray-800">
          {bad.length
            ? t('{n} scénario(s) à regarder', { n: String(bad.length) })
            : t('Toutes les règles tiennent.')}
          <span className="font-normal text-gray-400">
            {' '}· {t('trait pointillé = vérification par mots-clés, peut se tromper ; « – » = rien à juger')}
          </span>
        </p>
      )}

      {/* Failures first — the whole point is to read it in a minute. */}
      {[...rows].sort((a, b) => Number(failures(b.run).length || !!b.error)
        - Number(failures(a.run).length || !!a.error)).map((r) => (
        <div key={r.def.id}
          className={`mt-2 rounded-lg border p-3 ${
            r.error || failures(r.run).length ? 'border-red-200 bg-red-50/40' : 'border-gray-200 bg-gray-50/60'
          }`}>
          <p className="text-[12px] font-semibold text-gray-900">
            {r.def.title}
            <span className="font-normal text-gray-500"> — {r.def.intent}</span>
          </p>
          <p className="text-[11.5px] text-gray-700 bg-green-50 border border-green-100 rounded px-2 py-1 mt-1.5 whitespace-pre-wrap">
            {r.def.messages.join('\n')}
          </p>
          {r.error && <p className="text-[11.5px] text-red-700 mt-1.5">{r.error}</p>}
          {r.run && (
            <>
              <p className="text-[11.5px] text-gray-800 bg-white border border-gray-200 rounded px-2 py-1 mt-1.5 whitespace-pre-wrap">
                {r.run.reply || <i>{t('(aucune réponse)')}</i>}
              </p>
              {r.run.needs_human && (
                <p className="text-[10.5px] text-amber-700 mt-1">{t('→ demande un humain')}</p>
              )}
              <div className="flex gap-1.5 flex-wrap mt-2">
                {r.run.results.map((x) => <Chip key={x.rule} {...x} />)}
              </div>
            </>
          )}
          {!r.run && !r.error && running && (
            <p className="text-[11px] text-gray-400 mt-1.5">{t('en attente…')}</p>
          )}
        </div>
      ))}
    </div>
    </SettingGroup>
  );
}
